import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  ActivityIndicator,
  FlatList,
  StatusBar,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import Image from "@components/common/Image";
import BackArrow from "@assets/svg/BackArrow";
import VideoOverlayModal from "@components/VideoOverlayModal";
import AppText from "@components/AppText";
import VideoPlayer from "@components/VideoPlayer";
import colors from "@constants/color";
import metrics from "@constants/metrics";
import analytics from "@services/analytics";
import YoutubePlayer from "@components/YouTubePlayer";
import Button from "../../components/Button";
import FetchCMSEntity from "../../components/CMS/FetchCMSEntity";
import useGetVideoTime, {
  GetVideoTimeContextProvider,
} from "../../utils/useGetVideoTime";
import { getCMSEntityById } from "../../services/cmsV2-api";
import { LEADER_TRAINING } from "../../constants/analyticsEventsCT";
// import Orientation from "react-native-orientation-locker";

import t from "@constants/localization";

const PATH = "screens/LeaderTraining/TrainingVideoListing";

const TrainingVideoListing = (props) => {
  const { route: { params: { cms_item_id = "", cl_title = "" } = {} } = {} } =
    props;

  const videoBannerList = useRef([]);

  const [shouldLoadMore, setShouldLoadMore] = useState(true);
  const [refreshFlatList, setRefreshFlatList] = useState(true);
  const [endOfResults, setEndOfResults] = useState(false);

  const fetchTrainingData = useCallback(async () => {
    try {
      const { data: response } = await getCMSEntityById({
        id: cms_item_id,
        offset: 0,
      });
      videoBannerList.current = response.list_items;
      setRefreshFlatList((r) => !r);
    } catch (e) {
      console.log(e);
    }
  }, [cms_item_id]);

  useEffect(() => {
    fetchTrainingData();
  }, [fetchTrainingData]);

  useEffect(() => {
    cl_title &&
      analytics.trackEvent(LEADER_TRAINING.LISTING_LANDING, {
        list_name: cl_title,
      });
  }, [cl_title]);

  const loadMore = async () => {
    if (videoBannerList.current.length === 0 || !shouldLoadMore) {
      return;
    }
    try {
      const { data } = await getCMSEntityById({
        id: cms_item_id,
        offset: videoBannerList.current.length,
        limit: 5,
      });
      if (data.list_items.length === 0 || !shouldLoadMore) {
        setEndOfResults(true);
        setShouldLoadMore(false);
      } else {
        videoBannerList.current = videoBannerList.current.concat(
          data.list_items,
        );
        setRefreshFlatList(!refreshFlatList);
      }
    } catch (e) {
      setEndOfResults(true);
      setShouldLoadMore(false);
      console.log(e);
    }
  };

  const _renderItem = useCallback(({ item }) => {
    return (
      <FetchCMSEntity
        type={item.type}
        id={item.cms_item_id}
        fromTrainingVideoListing={true}
      />
    );
  }, []);

  const _ListFooterComponent = useCallback(() => {
    return <InfiniteFlatListFooter endOfResults={endOfResults} />;
  }, [endOfResults]);

  return (
    <>
      <View style={styles.topNavContainer}>
        <View style={styles.topNav}>
          <TouchableOpacity
            onPress={() => {
              props?.navigation &&
                typeof props.navigation.goBack === "function" &&
                props.navigation.goBack();
            }}
            style={styles.arrowCont}
          >
            <BackArrow />
          </TouchableOpacity>
          <AppText
            bold
            fontType="head1"
            style={styles.textLeftPadding}
            ellipsizeMode="tail"
            numberOfLines={1}
          >
            {cl_title || t(`${PATH}/leadersTrainingVideos`)}
          </AppText>
        </View>
      </View>

      <FlatList
        data={videoBannerList.current}
        extraData={refreshFlatList}
        renderItem={_renderItem}
        onEndReached={loadMore}
        ListFooterComponent={_ListFooterComponent}
        keyExtractor={(item, index) => `${item.cms_item_id}-${index}`}
        style={styles.flatListStyles}
        contentContainerStyle={styles.paddingHorizontal}
      />
    </>
  );
};

export const VideoListingItem = (props) => {
  const { title_ref = "", image_link = "", video_link = "", sub_type } = props;

  const [show, setShow] = useState(false);
  const [played, setPlayed] = useState(false);

  const imgSource =
    image_link.charAt(0) === "{"
      ? JSON.parse(image_link).file.response.url
      : image_link;

  useEffect(() => {
    if (show && !played) {
      setPlayed(true);
      analytics.trackEvent(LEADER_TRAINING.VIDEO_PLAYED, {
        video_name: title_ref,
      });
    }
  }, [played, show, title_ref]);

  return (
    <>
      <VideoOverlayModal
        visible={show}
        onClose={() => {
          setShow(!show);
          StatusBar.setHidden(false);
          StatusBar.setBarStyle("dark-content");
          // Orientation.lockToPortrait()
        }}
      >
        {sub_type === "youtube_video" ? (
          <YoutubePlayer {...props} inline={false} />
        ) : (
          <GetVideoTimeContextProvider>
            <VideoContainer
              {...{ video_link }}
              handleCta={() => {
                setShow(!show);
                StatusBar.setHidden(false);
                StatusBar.setBarStyle("dark-content");
                // Orientation.lockToPortrait()
              }}
            />
          </GetVideoTimeContextProvider>
        )}
      </VideoOverlayModal>
      <TouchableOpacity
        style={styles.videoListingContainer}
        onPress={() => {
          setShow(!show);
          StatusBar.setHidden(true);
        }}
      >
        <Image source={{ uri: imgSource }} style={styles.imageStyles} />
        <View style={styles.titleContainer}>
          <Text
            numberOfLines={2}
            ellipsizeMode="tail"
            style={styles.titleStyles}
          >
            {title_ref}
          </Text>
        </View>
      </TouchableOpacity>
    </>
  );
};

const VideoContainer = ({ video_link = "", handleCta = () => {} }) => {
  const [state] = useGetVideoTime();

  return (
    <>
      <VideoPlayer videoLink={video_link} />

      {state.isCompleted && (
        <View style={styles.videoStateCompleteContainer}>
          <Button
            style={styles.paddingHorizontal}
            buttonWidth="100%"
            btnText="Close"
            buttonFontType="head1"
            height={56}
            borderRadius={10}
            onPress={handleCta}
          />
        </View>
      )}
    </>
  );
};

const InfiniteFlatListFooter = ({ endOfResults }) => {
  return (
    <View style={styles.flatListFooter}>
      {!endOfResults && (
        <ActivityIndicator color={colors.primary} size="large" />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  topNavContainer: {
    flexDirection: "row",
    paddingVertical: 18,
    paddingHorizontal: metrics.mainContainerPadding,
    backgroundColor: colors.white,
    alignItems: "center",
    alignContent: "space-around",
    justifyContent: "space-between",
  },
  topNav: { flexDirection: "row", alignItems: "center" },
  textLeftPadding: {
    paddingLeft: 36,
  },
  videoListingContainer: {
    elevation: 1,
    borderRadius: 10,
    padding: 4,
    flexDirection: "row",
    backgroundColor: "white",
    width: "100%",
    marginVertical: 10,
  },
  videoStateCompleteContainer: {
    position: "absolute",
    bottom: 20,
    left: 20,
    right: 20,
  },
  flatListStyles: {
    flex: 1,
    backgroundColor: "#f7f7f7",
  },
  paddingHorizontal: {
    paddingHorizontal: 15,
  },
  flatListFooter: {
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
    height: 50,
  },
  imageStyles: {
    height: 84,
    width: 143,
    borderRadius: 10,
  },
  paddingLeft: { paddingLeft: 10 },
  titleStyles: {
    paddingVertical: 6,
    flex: 1,
    fontSize: 14,
    lineHeight: 16,
    flexWrap: "wrap",
    marginTop: 16,
    width: "100%",
  },
  titleContainer: {
    flexDirection: "row",
    flexGrow: 1,
    paddingHorizontal: 8,
  },
});

export default TrainingVideoListing;
